import { Button } from "@progress/kendo-react-buttons";
import { SvgIcon } from "@progress/kendo-react-common";
import { Input } from "@progress/kendo-react-inputs";
import {
    chartLineMarkersIcon,
    dollarIcon,
    paperPlaneIcon,
    percentIcon,
    sparklesIcon
} from "@progress/kendo-svg-icons";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useStartChatConversationMutation } from "../../../api/chatApi";
import RecentConversationsList from "../../../chat/recentConversationsList";

const planningTags = {
    Price: {
        icon: dollarIcon,
        className: "text-primary border-primary-subtle bg-primary-subtle"
    },
    Promotion: {
        icon: percentIcon,
        className: "text-info-emphasis border-info-subtle bg-info-subtle"
    },
    "Retail Media": {
        icon: chartLineMarkersIcon,
        className: "text-warning-emphasis border-warning-subtle bg-warning-subtle"
    }
} as const;

type PlanningTag = keyof typeof planningTags;

const promptCards: {
    title: string;
    description: string;
    tags: PlanningTag[];
}[] = [
    {
        title: "Optimise Commercial Strategy",
        description: "\"What is the optimal mix of price, promotion and retail media for this SKU over the next 12 weeks?\"",
        tags: ["Price", "Promotion", "Retail Media"]
    },
    {
        title: "Promotion vs Retail Media",
        description: "\"Should I invest more in promotion or retail media to maximise profit?\"",
        tags: ["Promotion", "Retail Media"]
    },
    {
        title: "Price Optimisation",
        description: "\"What base price should this SKU be to maximise margin while staying competitive?\"",
        tags: ["Price"]
    }
];

const Planning: React.FC = () => {
    const navigate = useNavigate();
    const [prompt, setPrompt] = useState("");
    const [startChatConversation, { isLoading: isSubmittingPrompt }] = useStartChatConversationMutation();
    const isSubmittingPromptRef = useRef(false);

    const navigateToConversation = (conversationId: number): void => {
        const searchParams = new URLSearchParams({
            id: conversationId.toString()
        });

        void navigate(`../simulator?${searchParams.toString()}`);
    };

    const handlePromptSubmit = async (): Promise<void> => {
        const trimmedPrompt = prompt.trim();
        if (!trimmedPrompt || isSubmittingPrompt || isSubmittingPromptRef.current) {
            return;
        }

        isSubmittingPromptRef.current = true;

        try {
            const response = await startChatConversation({ text: trimmedPrompt }).unwrap();
            setPrompt("");
            navigateToConversation(response.chat_conversation_id);
        } catch (error) {
            console.error("Error starting simulator conversation from planning prompt.", error);
        } finally {
            isSubmittingPromptRef.current = false;
        }
    };

    const handlePromptFormSubmit = (event: React.SubmitEvent<HTMLFormElement>): void => {
        event.preventDefault();
        void handlePromptSubmit();
    };

    const handleConversationSelected = (conversationId: number): void => {
        navigateToConversation(conversationId);
    };

    return (
        <div className="flex-grow-1 min-h-0 overflow-x-hidden overflow-y-auto">
            <div className="container-xl py-4">
                <div className="text-center mb-4">
                    <h1 className="display-4 fw-bold mb-3">Commercial planning, simulated instantly</h1>

                    <p className="fs-3 text-secondary mb-4">
                        Ask Sovra to optimise pricing, promotions and retail media investment for every SKU.
                    </p>

                    <div className="d-flex justify-content-center align-items-center flex-wrap gap-2">
                        {(Object.keys(planningTags) as PlanningTag[]).map(tag => (
                            <span
                                key={tag}
                                className={`badge rounded-pill border d-inline-flex align-items-center gap-2 px-3 py-2 fs-6 ${planningTags[tag].className}`}
                            >
                                <SvgIcon icon={planningTags[tag].icon} size="small" />
                                {tag}
                            </span>
                        ))}
                    </div>
                </div>

                <div className="mb-4">
                    <form onSubmit={handlePromptFormSubmit} className="shadow-sm mb-3">
                        <div className="input-group input-group-lg">
                            <span className="input-group-text bg-white border-end-0">
                                <SvgIcon icon={sparklesIcon} />
                            </span>
                            <Input
                                value={prompt}
                                disabled={isSubmittingPrompt}
                                className="form-control border-start-0"
                                placeholder="Ask Sovra how to optimise price, promotion and retail media..."
                                aria-label="Planning prompt"
                                onChange={event => setPrompt((event.value as string) ?? "")}
                            />
                            <Button
                                type="submit"
                                themeColor="primary"
                                fillMode="solid"
                                className="d-inline-flex align-items-center gap-2"
                                aria-label="Send prompt"
                                disabled={!prompt.trim() || isSubmittingPrompt}
                            >
                                <SvgIcon icon={paperPlaneIcon} />
                            </Button>
                        </div>
                    </form>

                    <RecentConversationsList onChange={handleConversationSelected} />
                </div>

                <div className="row g-3">
                    {promptCards.map(card => (
                        <div key={card.title} className="col-12 col-lg-4">
                            <div className="card h-100 shadow-sm border-0">
                                <div className="card-body d-flex flex-column p-4">
                                    <h2 className="h5 text-success fw-bold mb-3">{card.title}</h2>
                                    <p className="fs-5 text-secondary flex-grow-1">{card.description}</p>
                                    <div className="d-flex flex-wrap gap-2 mt-2">
                                        {card.tags.map(tag => (
                                            <span
                                                key={`${card.title}-${tag}`}
                                                className={`badge rounded-pill border d-inline-flex align-items-center gap-2 px-3 py-2 fs-6 ${planningTags[tag].className}`}
                                            >
                                                <SvgIcon icon={planningTags[tag].icon} size="small" />
                                                {tag}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default Planning;
